import React, {useEffect, useState} from "react";
import {Button, Grid, Link, Paper, TableCell, TableRow} from "@material-ui/core"
import PastRealEstateInvestment from "../../../shared/PastRealEstateInvestment";
import useFetchableData from "../../../../../hooks/useFetchableData";
import RectangularSkeleton from "../../../shared/Skeleton/RectangularSkeleton";
import SortableTable from "../../../shared/Tables/SortableTable";
import FormDialog from "../../../shared/Dialogs/FormDialog";
import {formatDateUS, humanizeMoney} from "../../../../../helpers/utils";
import CustomSnackbar from "../../../../user/shared/CustomSnackbar";
import useSnackbar from "../../../../../hooks/useSnackbar";
import useInvestorDeleter from "../../hooks/useInvestorDeleter";
import useInvestorUpdater from "../../hooks/useInvestorUpdater";

const RealEstateInvestmentHistorySection = (
    {
        isEditing, handleOnChange, handleOnChangeInField,
        handleOnError, session, investmentStyles,
        fetchRealEstateInvestments, ...props
    }
) => {
    const [realEstateInvestmentHistoryErrors, setRealEstateInvestmentHistoryErrors] = useState([]);
    const [selectedRealEstateInvestmentHistory, setSelectedRealEstateInvestmentHistory] = useState(null);
    const [realEstateInvestmentHistory, setRealEstateInvestmentHistory] = useState([]);
    const [tableRealEstateInvestmentHistory, setTableRealEstateInvestmentHistory] = useState([]);
    const {data: realEstateInvestmentHistoryData, setData: setRealEstateInvestmentHistoryData, initialFetch} = useFetchableData(fetchRealEstateInvestments, "real_estate_investment_history");
    const snackbar = useSnackbar();
    const updater = useInvestorUpdater(session, snackbar);
    const deleter = useInvestorDeleter(session, snackbar);

    useEffect(() => {
        handleOnChangeInField("tracked_real_estate_investments_attributes", [...realEstateInvestmentHistory], initialFetch)
    }, [realEstateInvestmentHistory])

    useEffect(() => {
        setRealEstateInvestmentHistory(realEstateInvestmentHistoryData)
        setTableRealEstateInvestmentHistory(realEstateInvestmentHistoryData.filter((realEstateInvestment) => !realEstateInvestment._destroy && !realEstateInvestment.key));
        setRealEstateInvestmentHistoryErrors(Array(realEstateInvestmentHistoryData.length).fill(false))
    }, [realEstateInvestmentHistoryData])

    const setRealEstateInvestmentHistoryError = (index, error) => {
        let newErrors = [...realEstateInvestmentHistoryErrors];
        newErrors[index] = error;
        setRealEstateInvestmentHistoryErrors(newErrors);
    }

    useEffect(() => {
        handleOnError(realEstateInvestmentHistoryErrors.some((error) => error))
    }, [realEstateInvestmentHistoryErrors]);

    const handleAddRealEstateInvestmentHistory = () => {
        let newRealEstateInvestmentHistory = [...realEstateInvestmentHistory];

        newRealEstateInvestmentHistory.push({
            real_estate_investment_date: undefined,
            total_raised: undefined,
            amount_invested: undefined,
            funded_property_id: undefined,
            investment_style_id: undefined,
            property_type_id:undefined,
            real_estate_investment_style_id: undefined,
            id: undefined,
            key: Date.now(),
        })

        setRealEstateInvestmentHistory(newRealEstateInvestmentHistory);
        setRealEstateInvestmentHistoryErrors(prevState => ([...prevState, true]))
    }

    const handleRemoveRealEstateInvestmentHistory = async (event, realEstateInvestment) => {
        event.stopPropagation();
        if (window.confirm(`Are you sure you want to delete this Real Estate Investment?`)) {
            await deleter.realEstateInvestments(realEstateInvestment);
            const newRealEstateInvestmentHistory = realEstateInvestmentHistory.filter((i) => i.id !== realEstateInvestment.id);
            const deletedIndex = realEstateInvestmentHistory.findIndex((i) => i.id === realEstateInvestment.id);
            const newRealEstateInvestmentHistoryErrors = realEstateInvestmentHistoryErrors.filter((error, index) => index !== deletedIndex);
            setRealEstateInvestmentHistory(newRealEstateInvestmentHistory);
            setRealEstateInvestmentHistoryErrors(newRealEstateInvestmentHistoryErrors)
            handleOnError(false);
            setSelectedRealEstateInvestmentHistory(null);
        }
    }

    const removeError = (index) => setRealEstateInvestmentHistoryErrors(realEstateInvestmentHistoryErrors.filter((error, i) => index !== i));

    const handleSaveRealEstateInvestment = async (pastRealEstateInvestment) =>{
        await updater.realEstateInvestments((pastRealEstateInvestment))
        pastRealEstateInvestment.funded_property_name = pastRealEstateInvestment.funded_property?.name;
        setRealEstateInvestmentHistory(realEstateInvestmentHistory.map(item => item.id === pastRealEstateInvestment.id ? pastRealEstateInvestment : item));
        setTableRealEstateInvestmentHistory(tableRealEstateInvestmentHistory.map(item => item.id === pastRealEstateInvestment.id ? pastRealEstateInvestment : item));
        setSelectedRealEstateInvestmentHistory(null);
    }

    return (
        <Grid container item xs={12}>
            <RectangularSkeleton loading={!initialFetch} width={1000} height={300}>
                <Paper className="form-container" elevation={3}>
                    <Grid item xs={12}>
                        <h3>Real Estate Investment History</h3>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            {
                                tableRealEstateInvestmentHistory.length > 0 && <SortableTable
                                    headCells={[
                                        { id: 'funded_property_name', numeric: false, disablePadding: false, label: 'Funded Property', sortable: true },
                                        { id: 'real_estate_investment_date', numeric: false, disablePadding: false, label: 'Date', sortable: true },
                                        { id: 'amount_invested', numeric: false, disablePadding: false, label: 'Amount Invested', sortable: true },
                                        { id: 'total_raised', numeric: false, disablePadding: false, label: 'Transaction Value', sortable: true },
                                        { id: 'options', numeric: false, disablePadding: false, label: null },
                                    ]}
                                    rows={tableRealEstateInvestmentHistory}
                                    RowRenderer={
                                        (row, _isSelected, index, _handleCheck) => (<TableRow
                                            hover
                                            tabIndex={-1}
                                            key={row.id}
                                            style={{cursor: "pointer"}}
                                            onClick={(event) => setSelectedRealEstateInvestmentHistory(row.id)}
                                        >
                                            <TableCell>
                                                {row.funded_property_name}
                                            </TableCell>
                                            <TableCell>
                                                {formatDateUS(row.real_estate_investment_date)}
                                            </TableCell>
                                            <TableCell>
                                                {humanizeMoney(row.amount_invested)}
                                            </TableCell>
                                            <TableCell>
                                                {humanizeMoney(row.total_raised)}
                                            </TableCell>
                                            <TableCell>
                                                <Link onClick={(event) => handleRemoveRealEstateInvestmentHistory(event, row)}>
                                                    Delete
                                                </Link>
                                            </TableCell>
                                        </TableRow>)
                                    }
                                    initialOrderBy={"funded_property_name"}
                                />
                            }
                        </Grid>
                        <Grid item xs={12}>
                            <Button color="primary" variant="contained" onClick={handleAddRealEstateInvestmentHistory} style={{marginBottom: '20px'}}>Add Real Estate Investment</Button>
                        </Grid>
                        <Grid item xs={12}>
                            {
                                realEstateInvestmentHistory.map((pastRealEstateInvestment, index) => (<React.Fragment key={pastRealEstateInvestment.id || pastRealEstateInvestment.key}>
                                        { !!pastRealEstateInvestment.key ? <PastRealEstateInvestment
                                            key={pastRealEstateInvestment.id || pastRealEstateInvestment.key}
                                            session={session}
                                            pastRealEstateInvestmentData={pastRealEstateInvestment}
                                            index={index}
                                            realEstateInvestmentHistory={realEstateInvestmentHistory}
                                            setRealEstateInvestmentHistory={setRealEstateInvestmentHistory}
                                            removeRealEstateInvestmentError={(i) => removeError(i)}
                                            investmentStyles={investmentStyles}
                                            handleOnError={(error) => setRealEstateInvestmentHistoryError(index, error)}
                                            isEditing={isEditing}
                                            deleter={deleter}
                                            isNew
                                        /> : <FormDialog
                                            open={selectedRealEstateInvestmentHistory === pastRealEstateInvestment.id}
                                            label={"Real Estate Investment History Form"}
                                            handleSave={() => handleSaveRealEstateInvestment(pastRealEstateInvestment)}
                                            handleClose={() => setSelectedRealEstateInvestmentHistory(null)}
                                        >
                                            <PastRealEstateInvestment
                                                key={pastRealEstateInvestment.id || pastRealEstateInvestment.key}
                                                session={session}
                                                pastRealEstateInvestmentData={pastRealEstateInvestment}
                                                index={index}
                                                realEstateInvestmentHistory={realEstateInvestmentHistory}
                                                setRealEstateInvestmentHistory={setRealEstateInvestmentHistory}
                                                removeRealEstateInvestmentError={(i) => removeError(i)}
                                                investmentStyles={investmentStyles}
                                                handleOnError={(error) => setRealEstateInvestmentHistoryError(index, error)}
                                                isEditing={isEditing}
                                                deleter={deleter}
                                                setSelectedRealEstateInvestmentHistory={setSelectedRealEstateInvestmentHistory}
                                            />
                                        </FormDialog>
                                        }

                                        <CustomSnackbar
                                            open={snackbar.open}
                                            closeHandler={snackbar.close}
                                            message={snackbar.message}
                                            options={{
                                                severity: 'success'
                                            }}
                                        />
                                    </React.Fragment>)
                                )
                            }
                        </Grid>
                    </Grid>
                </Paper>
            </RectangularSkeleton>
        </Grid>
    )
}

export default RealEstateInvestmentHistorySection;